import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Close, SlideshowOutlined } from '@mui/icons-material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHistory, useLocation, useParams } from 'react-router';

import ContactSupportWidget from '../../../app/appHeader/customerSupportWidget/CustomerSupportWidget';
import { RenderLogo } from '../../../app/appHeader/helpers';
import { VideaButton } from '../../../common-components/elements/VideaButton/VideaButton';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { showSegmentationAtom } from '../../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { usePreferences } from '../../../react-query/usePreferences';
import routes from '../../../routes/routes.constants';
import { theme } from '../../../theme/theme';
import { blueBlack640, blueBlack700 } from '../../../theme/videa-style-tokens';
import { isValidToothNumber } from '../../../utils/finding';
import { useIsEmbedded } from '../../../utils/hooks';
import { selectQuadrantsAtom, selectTeethAtom } from '../../patientList/atoms';
import annotatorConstants from '../annotator.constants';
import { imageFilterAtom, viewerModeAtom } from '../atoms';
import {
  enabledImageTypesAtom,
  imageBrowserOpenAtom,
} from '../multiFileViewer/findingViewer/imageBrowser/atoms';

import {
  annotateActionAtom,
  casePresentationModeAtom,
  hiddenAnnotationsAtom,
  selectedAnnotationAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
} from './atoms';

const Container = styled.div`
  z-index: 10;
  box-sizing: border-box;
  position: absolute;
  left: 0;
  right: 23em;
  width: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${theme.typography.fontFamily};
  right: 0.5em;

  .application-path-container {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 0.75em;
    width: 100%;
    box-sizing: border-box;
    height: 3.5em;

    @media (max-width: 1200px) {
      font-size: 0.8em;
    }

    @media (max-width: 800px) {
      font-size: 0.675em;
    }

    .application-path-group {
      width: 100%;
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      padding: 0.5em;

      .left-side {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 2em;
      }

      .right-side {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 1em;

        .logo {
          display: flex;
          align-items: center;
          justify-content: start;
          width: 113px;
          height: 50px;
        }
      }
    }

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 20em;
      gap: 0.5em;
      color: ${blueBlack640};
      font-size: 1.125em;
    }

    .exit-button {
      background-color: ${blueBlack700};
      height: 2.5em;
    }
  }
`;

export const CasePresentationHeader = () => {
  const { id: selectedPatientId, imageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const setCasePresentationMode = useSetAtom(casePresentationModeAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const setSelectedFindings = useSetAtom(selectedFindingsAtom);
  const setImageBrowserOpen = useSetAtom(imageBrowserOpenAtom);
  const setViewerMode = useSetAtom(viewerModeAtom);
  const setSelectedTeeth = useSetAtom(selectTeethAtom);
  const setSelectedQuadrants = useSetAtom(selectQuadrantsAtom);
  const setAnnotateAction = useSetAtom(annotateActionAtom);
  const setEnabledImageTypes = useSetAtom(enabledImageTypesAtom);
  const setHiddenAnnotations = useSetAtom(hiddenAnnotationsAtom);
  const setSelectedAnnotation = useSetAtom(selectedAnnotationAtom);
  const setSelectFindingImageId = useSetAtom(selectedFindingImageIdAtom);

  const currentImageFilter = useAtomValue(imageFilterAtom);

  const [originView, setOriginView] = useState<
    'FMX_VIEW' | 'SINGLE_FINDING_VIEW'
  >('SINGLE_FINDING_VIEW');

  const isEmbedded = useIsEmbedded();
  const { data: preferences } = usePreferences();
  const { search } = useLocation();

  const history = useHistory();
  const params = new URLSearchParams(search);

  const handleExit = () => {
    setAnnotateAction(undefined);
    setCasePresentationMode(false);
    setShowSegmentation(preferences?.showSegmentationView ?? false);
    setSelectedFindings(undefined);

    if (!preferences?.useSidebarRedesign) {
      setSelectedTeeth([]);
      setSelectedQuadrants([]);
    }

    setImageBrowserOpen(true);
    setEnabledImageTypes(['dentalPhotos', 'fmxPhotos', 'panoPhotos']);
    setViewerMode(annotatorConstants.MODES.VIEWING);
    setHiddenAnnotations([]);
    setSelectedAnnotation({ selectedAnnotation: '' });
    setSelectFindingImageId(undefined);

    params.delete('selected-photo');
    history.replace({ search: params.toString() });

    const newRoute = isEmbedded
      ? `/embedded${routes.ANNOTATOR}/${selectedPatientId}`
      : `${routes.ANNOTATOR}/${selectedPatientId}`;
    if (
      currentImageFilter === 'Odontogram' ||
      (currentImageFilter && isValidToothNumber(currentImageFilter))
    ) {
      history.push(newRoute);
      setImageBrowserOpen(false);
    } else if (originView === 'FMX_VIEW') {
      history.push(newRoute);
    }
  };

  useEffect(() => {
    if (!imageId) {
      setOriginView('FMX_VIEW');
    }
  }, [imageId]);

  return (
    <>
      <Container>
        <div className="application-path-container">
          <div className="application-path-group">
            <div className="left-side">
              <VideaButton
                className="exit-button"
                startIcon={<Close />}
                onClick={handleExit}
              >
                <Trans>Exit</Trans>
              </VideaButton>
              <div className="title">
                <SlideshowOutlined />
                <VideaTypography className="title" variant="labelSmallStrong">
                  <Trans>Presentation Mode</Trans>
                </VideaTypography>
              </div>
            </div>

            <div className="right-side">
              <RenderLogo />
              <ContactSupportWidget />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};
